// admin/login/AdminLogin.js
import {
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import React, { useState } from "react";
import { TranslateContext } from "../../context/TranslateContext";
import { GlobalContext } from "../../context/GlobalContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import ThemeToggle from "../../common/ThemeToggle";

const AdminLogin = () => {
  const history = useHistory();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { lang } = React.useContext(TranslateContext);
  const [showPassword, setShowPassword] = useState(false);
  const { hitAxios } = React.useContext(GlobalContext);
  const [state, setState] = React.useState({ email: "", password: "" });

  async function handleLogin() {
    const res = await hitAxios({
      path: "/api/admin/login",
      post: true,
      obj: state,
    });
    if (res.data.success) {
      localStorage.setItem(
        process.env.REACT_APP_TOKEN + "_admin",
        res.data.token,
      );
      history.push("/admin");
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      {/* Background blobs */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.12 : 0.06)} 0%, transparent 70%)`,
            filter: "blur(60px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-20%",
            left: "-10%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, isDark ? 0.1 : 0.05)} 0%, transparent 70%)`,
            filter: "blur(60px)",
          }}
        />
      </Box>

      {/* Theme toggle — top right */}
      <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 10 }}>
        <ThemeToggle />
      </Box>

      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        {/* Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4.5 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            backdropFilter: "blur(20px)",
            boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, isDark ? 0.5 : 0.1)}`,
          }}
        >
          {/* Icon + heading */}
          <Stack alignItems="center" spacing={1} sx={{ mb: 3.5 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "18px",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                mb: 0.5,
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight={800}
              color="text.primary"
              letterSpacing="-0.02em"
            >
              {lang?.adminLogin || "Admin Login"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {lang?.adminLoginSubtitle ||
                "Sign in to access the admin dashboard"}
            </Typography>
          </Stack>

          {/* Form */}
          <Stack spacing={2}>
            <TextField
              value={state.email}
              onChange={(e) => setState({ ...state, email: e.target.value })}
              fullWidth
              label={lang?.email || "Email"}
              placeholder="admin@example.com"
              type="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      sx={{ color: "text.disabled", fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              onChange={(e) => setState({ ...state, password: e.target.value })}
              value={state.password}
              label={lang?.password || "Password"}
              placeholder="••••••••"
              type={showPassword ? "text" : "password"}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{ color: "text.disabled", fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: "text.disabled" }}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              onClick={handleLogin}
              fullWidth
              variant="contained"
              size="large"
              disabled={!state.email.trim() || !state.password}
              sx={{ mt: 0.5, py: 1.4 }}
            >
              {lang?.signIn || "Sign In"}
            </Button>
          </Stack>

          {/* Divider + hint */}
          <Divider sx={{ my: 2.5 }}>
            <Typography variant="caption" color="text.disabled" sx={{ px: 1 }}>
              ADMIN PORTAL
            </Typography>
          </Divider>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, isDark ? 0.08 : 0.05),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <AdminPanelSettingsIcon
              sx={{
                fontSize: 16,
                color: "warning.main",
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              lineHeight={1.5}
            >
              {lang?.adminLoginWarning ||
                "This area is restricted to authorized administrators only."}
            </Typography>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 2.5,
            fontSize: "0.7rem",
          }}
        >
          {lang?.copyrightText ||
            `© ${new Date().getFullYear()} All rights reserved.`}
        </Typography>
      </Container>
    </Box>
  );
};

export default AdminLogin;
